import React, { useState, useEffect, useContext } from "react";
import { Box, Button, Typography, Grid, TextField } from "@mui/material";
import { getCompanysApi } from "../../../api/company";
import { registerUserApi } from "../../../api/user";
import { useUserRole } from "../../../hook/useUserRole.js";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import Popup from "../../../component/popup/Popup.js";
import SelectComponent from "../../../component/SelectComponent.js";
import AddNewCompanyPopup from "./AddNewCompanyPopup.js";

const textFieldStyle = {
    my: 1,
    ml: 1,
    flex: 1,
    borderRadius: "4px",
    background: "#fff",
    "& .MuiInputBase-input": {
        py: 0.5,
        px: 0,
    },
    "& .MuiInputBase-root": {
        px: 2,
        py: 0,
    },
};

const typographyStyle = {
    py: 1,
    fontWeight: 700,
    fontSize: "11px",
    minWidth: 100,
};

const buttonStyle = {
    borderRadius: "10px",
    lineHeight: "normal",
    my: 2,
};

const CreateUserPopup = ({ open, onClose, onSuccess, userTableData }) => {
    const userRoleOptions = useUserRole();
    const { showSuccess, showError } = useContext(NotificationContext);
    const renderLabel = (label) => (
        <Typography
            sx={typographyStyle}
            dangerouslySetInnerHTML={{ __html: label }}
        />
    );

    const renderTextField = (label, field, placeholder, customOnChange) => {
        const handleChange = (e) => {
            if (customOnChange) {
                customOnChange(e.target.value);
            } else {
                updateUserState(field, e.target.value);
            }
        };
        return (
            <Grid item xs={5} sx={{ display: "flex", alignItems: "center" }}>
                {renderLabel(label)}
                <TextField
                    value={userState[field] || ""}
                    onChange={handleChange}
                    placeholder={placeholder}
                    variant="outlined"
                    sx={textFieldStyle}
                />
            </Grid>
        );
    };

    const INITIAL_USER_STATE = {
        email: "",
        company: "",
        firstName: "",
        lastName: "",
        position: "",
        userRole: "",
    };

    const [userState, setUserState] = useState(INITIAL_USER_STATE);
    const [addNewCompanyPopup, setAddNewCompanyPopup] = useState(false);

    const [companyOptions, setCompanyOptions] = useState([]);
    const [filteredUserRoleOptions, setFilteredUserRoleOptions] = useState([]);

    const filterUserRolesByCompany = (isClpCompany, roles) => {
        if (!roles) return roles;
        if (isClpCompany) {
            return roles.filter(role => role.value !== 4);//排除Contractor
        } else {
            return roles.filter(role => [1, 4].includes(role.value));//System Administrator 和 Contractor
        }
    };

    useEffect(() => {
        const isClpCompany = userState.company === 2; //CLP
        const newFilteredRoles = filterUserRolesByCompany(isClpCompany, userRoleOptions);
        setFilteredUserRoleOptions(newFilteredRoles);
    }, [userState.company, userRoleOptions]);

    useEffect(() => {
        if (open) {
            setUserState(INITIAL_USER_STATE);
            getCompanyData();
        }
    }, [open]);

    const isFormValid = () => {
        const currentEmail = userState.email?.trim().toLowerCase();
        const isEmailUnique = !userTableData?.some(
            (item) => item.email?.trim().toLowerCase() === currentEmail
        );
        return (
            userState.email !== "" &&
            userState.company !== "" &&
            userState.firstName !== "" &&
            userState.lastName !== "" &&
            userState.userRole !== "" &&
            isEmailUnique
        );
    };

    const handleEmailChange = (value) => {
        setUserState((prevState) => ({
            ...prevState,
            email: value,
        }));
        if (!value.trim()) return;
        const normalize = (str) => str.replace(/\s+/g, "").toLowerCase();
        const isDuplicate = userTableData?.some(
            (item) =>
                normalize(item.email || "") === normalize(value)
        );
        if (isDuplicate) {
            showError("This Email Address has already been assigned.");
        }
    };

    const updateUserState = (field, value) => {
        setUserState((prevState) => ({
            ...prevState,
            [field]: value,
        }));
    };

    const createNewUser = async (event) => {
        event.preventDefault();
        try {
            const params = {
                email: userState.email,
                company: userState.company,
                firstName: userState.firstName,
                lastName: userState.lastName,
                position: userState.position,
                userRole: userState.userRole,
            };
            const result = await registerUserApi(params);
            if (result?.data) {
                onSuccess();
                showSuccess(
                    "New user has been successfully created and the user should received the email to reset the password."
                );
                setUserState(INITIAL_USER_STATE);
                onClose();
            }
        } catch {
            showError("Creation failed");
        }
    };

    const getCompanyData = async () => {
        try {
            const result = await getCompanysApi();
            if (result.data) {
                setCompanyOptions(result.data);
            }
        } catch {
            showError("Received company data occurred error.");
        }
    };

    return (
        <>
            <Popup
                open={open}
                onClose={onClose}
                popupStyle={{
                    minWidth: 1000,
                    maxWidth: 1000,
                }}
                modalStyle={{
                    "& .MuiBox-root.css-ft9d2q": {
                        borderRadius: "10px 10px 0 0",
                    },
                }}
                title="User Account Creation"
            >
                <Box maxHeight={800} overflow="auto">
                    <form onSubmit={createNewUser}>
                        <Box sx={{ ml: 4, mt: 2 }}>
                            <Grid container columnSpacing={3}>
                                {renderTextField(
                                    "* Email Address:",
                                    "email",
                                    "Please Input Email Address...",
                                    handleEmailChange
                                )}
                                <Grid item xs={5} display="flex" alignItems="center">
                                    <SelectComponent
                                        options={companyOptions}
                                        value={userState.company}
                                        onChange={(value) => {
                                            setUserState((prevState) => ({
                                                ...prevState,
                                                company: value,
                                                userRole: ""
                                            }));
                                        }}
                                        required={false}
                                        selectLabel="* Company:"
                                        labelStyle={{ fontSize: "11px", minWidth: 100, mr: 1 }}
                                        selectStyle={{ width: "100%" }}
                                        placeholder="Please Select Company..."
                                    />
                                </Grid>
                                <Grid item xs={2} display="flex" alignItems="center">
                                    <Button
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setAddNewCompanyPopup(true);
                                        }}
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ borderRadius: "10px", lineHeight: "normal" }}
                                    >
                                        Add Company
                                    </Button>
                                </Grid>
                                {renderTextField(
                                    "* First Name:",
                                    "firstName",
                                    "Please Input First Name..."
                                )}
                                {renderTextField(
                                    "&nbsp;&nbsp;&nbsp;Position:",
                                    "position",
                                    "Please Input Position..."
                                )}
                                {renderTextField(
                                    "* Last Name:",
                                    "lastName",
                                    "Please Input Last Name..."
                                )}
                                <Grid item xs={5} display="flex" alignItems="center">
                                    <SelectComponent
                                        // options={userRoleOptions}
                                        options={filteredUserRoleOptions}
                                        value={userState.userRole}
                                        onChange={(value) => {
                                            setUserState((prevState) => ({
                                                ...prevState,
                                                userRole: value,
                                            }));
                                        }}
                                        required={false}
                                        selectLabel="* User Role:"
                                        labelStyle={{ fontSize: "11px", minWidth: 100, mr: 1 }}
                                        selectStyle={{ width: "100%" }}
                                        placeholder="Please Select User Role..."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </form>
                </Box>
                <Box textAlign="center">
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!isFormValid()}
                        color={isFormValid() ? "success" : "primary"}
                        sx={buttonStyle}
                        onClick={createNewUser}
                    >
                        Create
                    </Button>
                </Box>
            </Popup>
            <AddNewCompanyPopup
                open={addNewCompanyPopup}
                onClose={() => setAddNewCompanyPopup(false)}
                options={companyOptions}
                onSetSuccess={(value) => {
                    getCompanyData();
                    showSuccess(value);
                }}
                onSetError={(value) => {
                    showError(value);
                }}
            />
        </>
    );
};

export default CreateUserPopup;
